import { Coins } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { EchoflicksVideoSession } from "../types";
import { resolveEchoCredits } from "./credits";

/**
 * Hero badge: what this echo cost the user in credits. Sits next to the
 * attribution source chip. Renders nothing on echoes older than the credit
 * projection (kind "unknown") — no guessing.
 */
export const EchoCreditsBadge = ({
  record,
}: {
  record: EchoflicksVideoSession;
}) => {
  const credits = resolveEchoCredits(record);
  if (credits.kind === "unknown") return null;

  const balanceBefore =
    typeof record.credits_balance_before === "number"
      ? ` · balance before: ${record.credits_balance_before.toLocaleString("en-US")}`
      : "";

  if (credits.kind === "free") {
    const nominal =
      credits.nominalCost != null && credits.nominalCost > 0
        ? `No credits deducted (free mode) — nominal price ${credits.nominalCost.toLocaleString("en-US")}`
        : "No credits were deducted for this echo";
    return (
      <Badge
        variant="outline"
        title={`${nominal}${balanceBefore}`}
        aria-label="Credits: free"
      >
        <Coins className="size-3" />
        Free
      </Badge>
    );
  }

  return (
    <Badge
      variant="outline"
      title={
        credits.cost != null
          ? `Deducted from the user's balance${balanceBefore}`
          : `Credits were deducted; the amount is unknown on this legacy echo${balanceBefore}`
      }
      aria-label={
        credits.cost != null
          ? `Credits: ${credits.cost} deducted`
          : "Credits: deducted"
      }
    >
      <Coins className="size-3" />
      {credits.cost != null
        ? `${credits.cost.toLocaleString("en-US")} credits`
        : "Charged"}
    </Badge>
  );
};
